import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";

import { DELETE_WORK, EDIT_WORK } from "../../constants";
import { CardLayout, DeleteModal, MTable } from "../v3";
import { ProjectWorkTableColumns } from "../../pages/project/constants";
import { HumanResourceService } from "../../services";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const ProjectWork = () => {
  const [works, setWorks] = useState([]);
  const [projectWorkWorkers, setProjectWorkWorkers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    sortModel,
    setSortModel,
    dateRange,
    setDateRange,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "project-works",
    defaults: { sortModel: [{ field: "date", sort: "desc" }] },
  });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, recordId: null });

  const canEditWork = useHasPermission(EDIT_WORK);
  const canDeleteWork = useHasPermission(DELETE_WORK);

  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const fetchData = useCallback(
    async (workFilters = {}) => {
      setLoading(true);

      try {
        const workResponse = await HumanResourceService.getWorks({
          ...workFilters,
          projectId: id,
        });

        setWorks(workResponse?.data?.rows);
        setTotalCount(workResponse?.data?.count);

        // gets the unique worker ids from the work response to get the workers names
        const workerIds = Array.from(new Set(workResponse?.data?.rows?.map((item) => item.workerId)));
        if (workerIds.length > 0) {
          const workersData = await HumanResourceService.getEmployees({
            type: "WORKER",
            ids: workerIds.join(","),
            limit: workerIds.length,
            includeDeleted: true,
          });
          setProjectWorkWorkers(workersData?.data?.rows);
        }
      } catch (error) {
        toast.error("Failed to fetch work statement");
        console.error("🚀 ~ fetchData error:", error);
      } finally {
        setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    if (!dateRange) return; // wait for DateRangeInput default/apply callback

    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      fromDate: dateRange.startDate,
      toDate: dateRange.endDate,
    });
  }, [id, paginationState, sortModel, fetchData, dateRange]);

  const handleDateRangeChange = (ranges) => {
    const selection = ranges?.[0];
    if (!selection?.startDate || !selection?.endDate) return;

    // WHY: Keep the existing API date format while replacing fixed date windows.
    setDateRange({
      startDate: moment(selection.startDate).startOf("day").format("YYYY-MM-DD"),
      endDate: moment(selection.endDate).endOf("day").format("YYYY-MM-DD"),
    });
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(ProjectWorkTableColumns());
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title="Works" columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Works",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const onViewClick = (params, event) => navigate(`/worker/${params.row.workerId}/work/${params.row.id}`);

  const openDeleteModal = (work) => {
    setDeleteModal({ isOpen: true, recordId: work.id });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, recordId: null });
  };

  const handleDelete = async () => {
    try {
      await HumanResourceService.deleteWork(deleteModal.recordId);
      toast.success("Work deleted successfully");
      fetchData({
        sortBy: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort,
        page: paginationState.page + 1,
        limit: paginationState.pageSize,
        fromDate: dateRange?.startDate,
        toDate: dateRange?.endDate,
      });
    } catch (error) {
      toast.error("Failed to delete work");
    } finally {
      closeDeleteModal();
    }
  };

  const handleActions = (workData, actionType) => {
    switch (actionType) {
      case "edit":
        navigate(`/edit-work/${workData.id}`);
        break;
      case "delete":
        openDeleteModal(workData);
        break;
      default:
        return null;
    }
  };

  const columnHeaders = ProjectWorkTableColumns(handleActions, canEditWork, canDeleteWork);

  const rows = works.map((work, index) => {
    const worker = projectWorkWorkers.find((worker) => worker.id === work.workerId);
    const isGroupWork = Boolean(work.isGroupWork);
    const wage = isGroupWork ? Number(work.totalAmount || 0) : Number(work.wage || 0);
    const overTime = isGroupWork ? 0 : Number(work.overtimeHours || 0) * Number(work.overtimeRate || 0);
    return {
      id: work.id,
      sl: index + 1 + paginationState.page * paginationState.pageSize,
      date: moment(work?.date).format("DD MMM YYYY"),
      type: isGroupWork ? "Group" : "Individual",
      workerName: worker?.name,
      wage: wage,
      overTime: overTime,
      total: wage + overTime,
      workerId: work?.workerId,
    };
  });

  const DEFAULT_SORT = [{ field: "date", sort: "desc" }];
  return (
    <>
      <CardLayout>
        <MTable
          // Date range data
          showDateRangePicker
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          // Export data
          showExportButton
          isPdfGenerating={isPdfGenerating}
          onExportPdfClick={handleDownloadPdfClick}
          // Table data
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount || 0}
          loading={loading}
          sortModel={sortModel}
          onSortChange={setSortModel}
          defaultSortModel={DEFAULT_SORT}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
          onRowClick={onViewClick}
        />
      </CardLayout>
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this work?"
      />
    </>
  );
};
